import React, { memo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Description, H1, RaisedButton, ThemeName } from "@evergis/ui";

import { Error } from "components/Icons/Error";
import { CardList } from "../components/CardList/CardList";
import { CardsBlock, CardsError } from "../components/Cards/styled";
import { TableList } from "../components/TableList/TableList";

import { getThemeName } from "../../../ducks/theme/selectors";
import { useInputResource } from "../hooks/useInputResource";
import { usePagination } from "../hooks/usePagination";
import { useViewType } from "../hooks/useViewType";

import { ResourceViewType } from "../components/ViewType/types";
import { Resources } from "../hooks/useFetchParams";
import { ResourceInfoDc } from "../types";

const FOOTER_HEIGHT = 40;
const THEAD_HEIGHT = 38;
const TABLE_ROW_HEIGHT = 52;
const CARD_WIDTH = 352;
const CARD_HEIGHT = 264;
const PAGINATION_HEIGHT = 44;
const HEADER_HEIGHT = 132;
const CARDS_CONTENT_PADDING = 32;
const VIEWPORT_HEIGHT_PADDING = window.outerHeight - window.innerHeight;

const AVAILABLE_CARDS_CONTENT_HEIGHT =
  screen.availHeight - FOOTER_HEIGHT - HEADER_HEIGHT - CARDS_CONTENT_PADDING * 2 - VIEWPORT_HEIGHT_PADDING;
const AVAILABLE_CARDS_CONTENT_WIDTH = screen.availWidth - CARDS_CONTENT_PADDING * 2;

const CARDS_PAGE_SIZE =
  Math.round(AVAILABLE_CARDS_CONTENT_HEIGHT / CARD_HEIGHT) * Math.round(AVAILABLE_CARDS_CONTENT_WIDTH / CARD_WIDTH);

const AVAILABLE_LIST_CONTENT_HEIGHT =
  screen.availHeight -
  FOOTER_HEIGHT -
  HEADER_HEIGHT -
  PAGINATION_HEIGHT -
  THEAD_HEIGHT -
  TABLE_ROW_HEIGHT / 2 -
  VIEWPORT_HEIGHT_PADDING;

const LIST_PAGE_SIZE = Math.round(AVAILABLE_LIST_CONTENT_HEIGHT / TABLE_ROW_HEIGHT);

export const cardsWithResource = (resource: Resources) => {
  return memo(() => {
    const themeName = useSelector(getThemeName);
    const [viewType] = useViewType();
    const input = useInputResource();
    const { offset, limit, ...params } = input;
    const { data, error, refetch, loading } = resource.use({ input, waitInput: true });

    const resources = data?.items;

    const [items, setItems] = useState<ResourceInfoDc[]>([]);

    const { currentPage, onPageChange, setOffset } = usePagination(
      viewType !== ResourceViewType.Table ? CARDS_PAGE_SIZE * 2 : LIST_PAGE_SIZE,
    );

    useEffect(() => {
      if (viewType === ResourceViewType.Table) {
        setItems([]);
      }
    }, [viewType]);

    useEffect(() => {
      setOffset(0);
      setItems([]);
    }, [JSON.stringify(params)]);

    useEffect(() => {
      if (viewType !== ResourceViewType.Table) {
        setOffset(0);
        setItems([]);
      }
    }, [data?.totalCount]);

    useEffect(() => {
      if (resources && viewType !== ResourceViewType.Table) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setItems(currentItems => [...currentItems, ...resources]);
      }
    }, [resources, viewType]);

    if (error) {
      return (
        <CardsError>
          <Error />
          <H1>Не удалось загрузить данные.</H1>
          <Description>Пожалуйста, обновите каталог.</Description>
          <RaisedButton primary onClick={refetch}>
            Обновить
          </RaisedButton>
        </CardsError>
      );
    }

    if (viewType === ResourceViewType.Table) {
      return (
        <TableList
          key={ResourceViewType.Table}
          resources={resources as ResourceInfoDc[]}
          totalCount={data?.totalCount}
          currentPage={currentPage}
          onPageChange={onPageChange}
          pageSize={LIST_PAGE_SIZE}
          loading={loading}
        />
      );
    }

    return (
      <CardsBlock key={ResourceViewType.Grid} $isDark={themeName === ThemeName.Dark} id="cardListContainer">
        <CardList
          resources={items as ResourceInfoDc[]}
          totalCount={data?.totalCount}
          currentPage={currentPage}
          onPageChange={onPageChange}
          loading={loading}
        />
      </CardsBlock>
    );
  });
};
